import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Background,
  ConnectionMode,
  Controls,
  Panel,
  ReactFlow,
  useEdgesState,
  useNodesState,
  useReactFlow,
  type EdgeChange,
  type EdgeTypes,
  type FinalConnectionState,
  type HandleType,
  type NodeTypes,
  type OnConnectEnd,
  type OnConnectStart,
  type OnSelectionChangeFunc,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { LayoutGrid, Pencil, Pin, PinOff, Plus, Redo2, RotateCcw, Save, StickyNote, Trash2, Undo2, Ungroup, Users } from 'lucide-react';
import type { ForeignKeyModel, Group, Note, NoteAttachment, ProjectOrigin } from '@bits2schema/shared';
import { getEdgeRoute, resolveForeignKeyEdges } from '@bits2schema/shared';
import { api } from '../api/client';
import { useCommandStack } from '../command/stack';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { useT } from '../i18n';
import {
  createAddForeignKeyCommand,
  createAddTableCommand,
  createDeleteForeignKeyCommand,
  createUpdateForeignKeyCommand,
} from '../schema/commands';
import { createDefaultTable } from '../schema/defaults';
import {
  areForeignKeyColumnTypesCompatible,
  computeForeignKeyCardinality,
  defaultForeignKeyName,
  ensureUniqueName,
} from '../schema/mutations';
import { DeleteColumnDialog } from '../schema/dialogs/DeleteColumnDialog';
import { DeleteTableDialog } from '../schema/dialogs/DeleteTableDialog';
import { useSchemaStore } from '../schema/store';
import { useWorkspaceSelection } from '../workspace/selection';
import { ContextMenu, ContextMenuItem } from './ContextMenu';
import {
  createAddTableToGroupCommand,
  createAutoLayoutCommand,
  createDeleteNoteCommand,
  createDissolveGroupCommand,
  createEditNoteTextCommand,
  createGroupCommand,
  createMoveGroupCommand,
  createMoveNoteCommand,
  createMoveTableCommand,
  createNoteCommand,
  createPinNoteCommand,
  createPlaceTableCommand,
  createRecolorTableCommand,
  createRemoveTableFromGroupCommand,
  createRenameGroupCommand,
  createResetEdgeRouteCommand,
  randomId,
} from './commands';
import { GroupNameDialog } from './dialogs/GroupNameDialog';
import { CanvasToolsMenu } from './CanvasToolsMenu';
import { CreateScriptPanel } from './CreateScriptPanel';
import { ExportPanel } from './ExportPanel';
import { ForeignKeyEdge } from './edges/ForeignKeyEdge';
import {
  NOTE_STACK_OFFSET,
  TABLE_NODE_FALLBACK_HEIGHT,
  TABLE_NODE_WIDTH,
  columnRowCenterOffset,
  computeGroupBoundingBox,
  estimateTableNodeHeight,
  fallbackTablePosition,
} from './geometry';
import { computeFullAutoLayout, computePlacementsForNewConnection } from './layout';
import { GroupFrameNode } from './nodes/GroupFrameNode';
import { NoteNode } from './nodes/NoteNode';
import { TableNode } from './nodes/TableNode';
import { ChangeScriptPanel } from './ChangeScriptPanel';
import { useCanvasStore } from './store';
import { SyncPanel, type SyncPanelHandle } from './SyncPanel';
import type { CanvasEdge, CanvasNode, DragOriginColumn, FkEdgeData, GroupFlowNode, NoteFlowNode, TableFlowNode } from './types';
import { ViewSettingsPanel } from './ViewSettingsPanel';

const nodeTypes: NodeTypes = { table: TableNode, groupFrame: GroupFrameNode, note: NoteNode };
const edgeTypes: EdgeTypes = { foreignKey: ForeignKeyEdge };

type MenuState =
  | { kind: 'table'; tableId: string; position: { x: number; y: number } }
  | { kind: 'column'; tableId: string; columnId: string; position: { x: number; y: number } }
  | { kind: 'group'; groupId: string; position: { x: number; y: number } }
  | { kind: 'note'; noteId: string; position: { x: number; y: number } }
  | { kind: 'edge'; edgeId: string; tableId: string; foreignKeyId: string; position: { x: number; y: number } }
  | { kind: 'pane'; flowPosition: { x: number; y: number }; position: { x: number; y: number } };

/** Krátké transientní vysvětlení odmítnutého tažení/přepojení FK (Epic 5, E5-US1 AC3). */
interface ConnectFeedback {
  position: { x: number; y: number };
  message: string;
}

const CONNECT_FEEDBACK_TIMEOUT_MS = 2500;

type GroupDialogState =
  | { mode: 'create'; tableIds: string[] }
  | { mode: 'rename'; groupId: string; currentName: string };

interface CanvasViewProps {
  projectId: string;
  projectName: string;
  projectOrigin: ProjectOrigin;
  /** Epic 15, E15-US4 — portal cíl pro `CanvasToolsMenu` v `.toolbar__actions` (`ProjectPage.tsx`). */
  toolsMenuSlot: HTMLDivElement | null;
}

/** Canvas panel wokspace layoutu (E11) — providery (`ReactFlowProvider`, `CanvasStoreProvider`,
 * `SchemaStoreProvider`, `WorkspaceSelectionProvider`) drží nadřazené komponenty (`Workspace.tsx`,
 * `ProjectPage.tsx`), protože je sdílí i strom a inspektor. */
export function CanvasView({ projectId, projectName, projectOrigin, toolsMenuSlot }: CanvasViewProps) {
  const t = useT();
  const { canvas, execute, updateViewSettings, saveStatus, saveNow } = useCanvasStore();
  const { schema, execute: schemaExecute } = useSchemaStore();
  const { canUndo, canRedo, undoEntry, redoEntry, undo, redo, runGroup } = useCommandStack();
  const { screenToFlowPosition, fitView, fitBounds } = useReactFlow();
  const [nodes, setNodes, onNodesChange] = useNodesState<CanvasNode>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<CanvasEdge>([]);
  const { selectedTableIds, setSelectedTableIds, inspectorTarget, setInspectorTarget, focusTarget, focusToken } =
    useWorkspaceSelection();
  const [menu, setMenu] = useState<MenuState | null>(null);
  const [groupDialog, setGroupDialog] = useState<GroupDialogState | null>(null);
  const [deleteTableId, setDeleteTableId] = useState<string | null>(null);
  const [deleteColumn, setDeleteColumn] = useState<{ tableId: string; columnId: string } | null>(null);
  const [dragOriginColumn, setDragOriginColumn] = useState<DragOriginColumn | null>(null);
  const [connectFeedback, setConnectFeedback] = useState<ConnectFeedback | null>(null);
  const [autoLayoutConfirmOpen, setAutoLayoutConfirmOpen] = useState(false);
  const [autoLayoutBusy, setAutoLayoutBusy] = useState(false);
  // Epic 9, E9-US7 — "Skript jsem spustil → synchronizovat" v `ChangeScriptPanel` otevře tenhle stejný `SyncPanel` scan.
  const syncPanelRef = useRef<SyncPanelHandle>(null);
  const requestSync = useCallback(() => syncPanelRef.current?.triggerScan(), []);

  const tableSizes = useMemo(() => {
    const sizes: Record<string, { width: number; height: number }> = {};
    for (const table of schema.tables) {
      sizes[table.id] = { width: TABLE_NODE_WIDTH, height: estimateTableNodeHeight(table, canvas.viewSettings) };
    }
    return sizes;
  }, [schema.tables, canvas.viewSettings]);

  // Pozice tabulky bez uloženého záznamu v `canvas.tablePositions` (nedotčená
  // ručním přesunem) dostává jen VYKRESLOVACÍ fallback (E6-US1 AC) — nikdy se
  // nezapisuje zpět do modelu. Skupina i poznámky ale musí počítat se stejnou
  // efektivní pozicí jako table node, jinak by obálka nově vytvořené skupiny
  // z ještě nepřesunutých tabulek vyšla prázdná (žádný člen by měl "reálnou"
  // pozici) a rámeček by se vůbec nevykreslil.
  const effectiveTablePositions = useMemo(() => {
    const positions: Record<string, { x: number; y: number }> = {};
    schema.tables.forEach((table, index) => {
      positions[table.id] = canvas.tablePositions[table.id] ?? fallbackTablePosition(index);
    });
    return positions;
  }, [schema.tables, canvas.tablePositions]);

  const groupBoxById = useMemo(() => {
    const boxes: Record<string, ReturnType<typeof computeGroupBoundingBox>> = {};
    for (const group of canvas.groups) {
      boxes[group.id] = computeGroupBoundingBox(group.memberTableIds, effectiveTablePositions, tableSizes);
    }
    return boxes;
  }, [canvas.groups, effectiveTablePositions, tableSizes]);

  const closeMenu = useCallback(() => setMenu(null), []);

  // Sdíleno mezi automatickým spouštěčem po prvním importu (E7-US1, beze
  // checkpointu/potvrzení) a ručním tlačítkem "Auto-layout" (E7-US2, s obojím)
  // — počítá NOVĚ (ne přes `execute()` per-table), jedno `execute()` volání
  // na konci = jeden vratný krok (E13-US4), stejný princip jako
  // `createMoveGroupCommand` u ručního tažení skupiny.
  const runFullAutoLayout = useCallback(async () => {
    const nextPositions = await computeFullAutoLayout(schema, canvas.groups, effectiveTablePositions, tableSizes);
    execute(createAutoLayoutCommand(canvas.tablePositions, nextPositions, canvas.edgeRoutes));

    // Nová rozložení mohou vzniknout kdekoliv v souřadném prostoru (ELK.js
    // nemá důvod respektovat aktuální viewport) — bez tohohle by uživatel po
    // kliknutí na "Auto-layout" koukal na prázdné plátno, dokud by ručně
    // neoddálil/nepřejel. Počítáno přímo z `nextPositions`/`tableSizes` (ne
    // přes `fitView({nodes})`), protože ten by četl RF vnitřní stav uzlů,
    // který se zaktualizuje až derive-efektem PO tomhle `execute()` — mohl by
    // se tak trefit do staré pozice, závislé na pořadí Reactích commitů.
    const positionEntries = Object.entries(nextPositions);
    if (positionEntries.length === 0) return;
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const [tableId, position] of positionEntries) {
      const size = tableSizes[tableId] ?? { width: TABLE_NODE_WIDTH, height: TABLE_NODE_FALLBACK_HEIGHT };
      minX = Math.min(minX, position.x);
      minY = Math.min(minY, position.y);
      maxX = Math.max(maxX, position.x + size.width);
      maxY = Math.max(maxY, position.y + size.height);
    }
    fitBounds({ x: minX, y: minY, width: maxX - minX, height: maxY - minY }, { duration: 400, padding: 0.2 });
  }, [schema, canvas.groups, canvas.tablePositions, canvas.edgeRoutes, effectiveTablePositions, tableSizes, execute, fitBounds]);

  // E7-US1 — ihned po dokončení prvního importu proběhne plný auto-layout bez
  // zásahu uživatele, bez potvrzení a bez checkpointu (kap. 2 rozhodnutí:
  // baseline `dbSync` záznam z Epic 1/3 canvas pozice nepotřebuje, do diffu se
  // nikdy nepromítají). `hasAutoLayoutRunRef` brání druhému spuštění při
  // StrictMode dvojím mountu v dev i při běžných re-renderech.
  const hasAutoLayoutRunRef = useRef(false);
  useEffect(() => {
    if (hasAutoLayoutRunRef.current) return;
    if (projectOrigin !== 'import') return;
    if (Object.keys(canvas.tablePositions).length > 0) return;
    if (schema.tables.length === 0) return;
    hasAutoLayoutRunRef.current = true;
    void runFullAutoLayout();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectOrigin, schema.tables.length]);

  // E7-US2 — ruční spuštění: potvrzovací dialog (přepíše veškerou ruční práci),
  // po potvrzení checkpoint PŘED přepočtem (`api.createCheckpoint`, stejný
  // endpoint jako `HistoryPanel`), teprve pak layout; `saveNow()` hned po
  // `execute()` místo čekání na 800ms debounce, aby pořadí checkpoint→nový
  // stav bylo reálné, ne jen eventuálně konzistentní.
  const confirmAutoLayout = useCallback(async () => {
    setAutoLayoutBusy(true);
    try {
      await api.createCheckpoint(projectId, t('history.checkpoint.beforeAutoLayout'));
      await runFullAutoLayout();
      saveNow();
    } finally {
      setAutoLayoutBusy(false);
      setAutoLayoutConfirmOpen(false);
    }
  }, [projectId, t, runFullAutoLayout, saveNow]);

  // Krátké vysvětlení odmítnutého tažení/přepojení (E5-US1 AC3) mizí samo po chvíli,
  // i bez dalšího kliknutí.
  useEffect(() => {
    if (!connectFeedback) return;
    const timeout = window.setTimeout(() => setConnectFeedback(null), CONNECT_FEEDBACK_TIMEOUT_MS);
    return () => window.clearTimeout(timeout);
  }, [connectFeedback]);

  const showConnectFeedback = useCallback((event: MouseEvent | TouchEvent, message: string) => {
    const point = 'clientX' in event ? event : (event as unknown as MouseEvent);
    setConnectFeedback({ position: { x: point.clientX, y: point.clientY }, message });
  }, []);

  // `nodes` mění identitu při každém derive-efektu níže; číst ho přes ref (ne jako
  // useCallback dependency) zabrání kruhové závislosti effect -> setNodes -> unpinNote -> effect.
  const nodesRef = useRef(nodes);
  useEffect(() => {
    nodesRef.current = nodes;
  }, [nodes]);

  // Stejný důvod jako u `nodesRef` výše, teď i pro hrany (Epic 5 — klik na FK
  // vazbu je nastaví RF interně jako `edge.selected`, mimo `execute()`). Bez
  // tohohle by derive-efekt níže (spouští se mj. těsně PO `onSelectionChange`,
  // protože sám mění `inspectorTarget`) hned zase přepsal `selected` na
  // `undefined` a vazba by se okamžitě "odvybrala" — stejná třída chyby, jaká
  // je popsaná u `preservedSelected` pro uzly.
  const edgesRef = useRef(edges);
  useEffect(() => {
    edgesRef.current = edges;
  }, [edges]);

  // Viz `handleConnectEnd`/`handleReconnectEnd` — RF gesto přepojení hrany
  // vždy doběhne i `onConnectEnd`, tenhle ref jim dá vědět, aby se nepočítalo
  // jako nová vazba navíc.
  const isReconnectingRef = useRef(false);
  const handleReconnectStart = useCallback(() => {
    isReconnectingRef.current = true;
  }, []);

  /** Odepnutí = poznámka dostane pozici, na které byla naposledy vykreslená (AC E6-US7). */
  const unpinNote = useCallback(
    (noteId: string) => {
      const note = canvas.notes.find((candidate) => candidate.id === noteId);
      if (!note || note.attachment.type === 'none') return;
      const renderedPosition = nodesRef.current.find((node) => node.id === noteId)?.position ?? { x: 0, y: 0 };
      execute(
        createPinNoteCommand(
          noteId,
          { attachment: note.attachment },
          { attachment: { type: 'none' }, position: renderedPosition },
        ),
      );
    },
    [canvas.notes, execute],
  );

  const changeNoteText = useCallback(
    (noteId: string, text: string) => {
      const note = canvas.notes.find((candidate) => candidate.id === noteId);
      if (note && note.text !== text) execute(createEditNoteTextCommand(noteId, note.text, text));
    },
    [canvas.notes, execute],
  );

  const deleteNote = useCallback(
    (noteId: string) => {
      const index = canvas.notes.findIndex((candidate) => candidate.id === noteId);
      if (index >= 0) execute(createDeleteNoteCommand(canvas.notes[index], index));
    },
    [canvas.notes, execute],
  );

  const openNotePinMenu = useCallback(
    (noteId: string, position: { x: number; y: number }) => setMenu({ kind: 'note', noteId, position }),
    [],
  );

  // Deriving nodes/edges from `canvas` + `schema` — `canvas` mění stav jen přes `execute()`
  // (na konci gesta), takže tento efekt nepřepíše RF spravovanou live pozici během tažení.
  //
  // RF vlastní `node.selected` (nastavené interní myší interakcí, ne přes `execute()`)
  // je TŘEBA sem převzít ze stávajících `nodesRef` — jinak by tenhle rebuild (spouští se
  // mj. i při změně `selectedTableIds`/`inspectorTarget`, tedy těsně PO onSelectionChange)
  // čerstvě nastavené `selected` smazal, RF by z toho vyvodil "výběr zmizel" a hned by
  // poslal další `onSelectionChange` s prázdným výběrem — nekonečná smyčka pro klik na
  // group/note uzel na canvasu (tabulky mají vlastní `isSelected`, tam to nevadí).
  useEffect(() => {
    const preservedSelected = (id: string) => nodesRef.current.find((node) => node.id === id)?.selected ?? false;
    const columnNotesById: Record<string, Note[]> = {};
    const notesByTable: Record<string, Note[]> = {};
    const notesByGroup: Record<string, Note[]> = {};
    for (const note of canvas.notes) {
      if (note.attachment.type === 'column') (columnNotesById[note.attachment.id] ??= []).push(note);
      else if (note.attachment.type === 'table') (notesByTable[note.attachment.id] ??= []).push(note);
      else if (note.attachment.type === 'group') (notesByGroup[note.attachment.id] ??= []).push(note);
    }

    const makeNoteNode = (note: Note, position: { x: number; y: number }): NoteFlowNode => ({
      id: note.id,
      type: 'note',
      position,
      draggable: note.attachment.type === 'none',
      selectable: note.attachment.type === 'none',
      selected: preservedSelected(note.id),
      data: {
        note,
        onChangeText: changeNoteText,
        onDelete: deleteNote,
        onUnpin: unpinNote,
        onOpenMenu: openNotePinMenu,
      },
    });

    const groupNodes: GroupFlowNode[] = canvas.groups.flatMap((group) => {
      const box = groupBoxById[group.id];
      if (!box) return [];
      return [
        {
          id: group.id,
          type: 'groupFrame' as const,
          position: { x: box.x, y: box.y },
          style: { width: box.width, height: box.height },
          zIndex: -1,
          selected: preservedSelected(group.id),
          data: {
            group,
            box,
            onOpenMenu: (target: { groupId: string; position: { x: number; y: number } }) =>
              setMenu({ kind: 'group', groupId: target.groupId, position: target.position }),
            onRename: (groupId: string) => {
              const current = canvas.groups.find((candidate) => candidate.id === groupId);
              if (current) setGroupDialog({ mode: 'rename', groupId, currentName: current.name });
            },
          },
        },
      ];
    });

    const tableNodes: TableFlowNode[] = schema.tables.map((table) => {
      const groupId = canvas.groups.find((group) => group.memberTableIds.includes(table.id))?.id ?? null;
      return {
        id: table.id,
        type: 'table' as const,
        position: effectiveTablePositions[table.id],
        selected: preservedSelected(table.id),
        data: {
          table,
          color: canvas.tableColors[table.id] ?? null,
          viewSettings: canvas.viewSettings,
          columnNotes: columnNotesById,
          tableNoteCount: notesByTable[table.id]?.length ?? 0,
          // Vlastní zvýraznění (ne RF `node.selected`) — sdílené s navigačním stromem
          // (Epic 11, E11-US3/US4), zdroj pravdy je `WorkspaceSelectionProvider`.
          isSelected: selectedTableIds.includes(table.id),
          highlightedColumnId:
            inspectorTarget.kind === 'column' && inspectorTarget.tableId === table.id
              ? inspectorTarget.columnId
              : null,
          dragOriginColumn,
          groupId,
          availableGroups: canvas.groups.filter((group) => group.id !== groupId),
          onRecolor: (tableId: string, color: TableFlowNode['data']['color']) =>
            execute(createRecolorTableCommand(tableId, canvas.tableColors[tableId] ?? null, color)),
          onOpenMenu: (target: { kind: 'table' | 'column'; tableId: string; columnId?: string; position: { x: number; y: number } }) =>
            setMenu(
              target.kind === 'table'
                ? { kind: 'table', tableId: target.tableId, position: target.position }
                : { kind: 'column', tableId: target.tableId, columnId: target.columnId!, position: target.position },
            ),
          onChangeNoteText: changeNoteText,
          onDeleteNote: deleteNote,
          onUnpinNote: unpinNote,
        },
      };
    });

    const noteNodes: NoteFlowNode[] = [];
    for (const [tableId, notesForTable] of Object.entries(notesByTable)) {
      const tablePosition = effectiveTablePositions[tableId];
      const size = tableSizes[tableId] ?? { width: TABLE_NODE_WIDTH, height: NOTE_STACK_OFFSET };
      notesForTable.forEach((note, index) => {
        noteNodes.push(
          makeNoteNode(note, { x: tablePosition.x + size.width + 24, y: tablePosition.y + index * NOTE_STACK_OFFSET }),
        );
      });
    }
    for (const [groupId, notesForGroup] of Object.entries(notesByGroup)) {
      const box = groupBoxById[groupId];
      if (!box) continue;
      notesForGroup.forEach((note, index) => {
        noteNodes.push(makeNoteNode(note, { x: box.x + box.width + 24, y: box.y + index * NOTE_STACK_OFFSET }));
      });
    }
    for (const note of canvas.notes) {
      if (note.attachment.type === 'none' && note.position) noteNodes.push(makeNoteNode(note, note.position));
    }

    setNodes([...groupNodes, ...tableNodes, ...noteNodes]);
    setEdges(
      resolveForeignKeyEdges(schema).flatMap((resolved) => {
        if (!resolved.targetTableId) return [];
        const sourceTable = schema.tables.find((table) => table.id === resolved.sourceTableId);
        const targetTable = schema.tables.find((table) => table.id === resolved.targetTableId);
        const sourceColumn = sourceTable?.columns.find((column) => column.name === resolved.foreignKey.columns[0]);
        const targetColumn = targetTable?.columns.find(
          (column) => column.name === resolved.foreignKey.referencedColumns[0],
        );
        const sourceRowOffset =
          sourceTable && sourceColumn ? columnRowCenterOffset(sourceTable, sourceColumn.id, canvas.viewSettings) : null;
        const targetRowOffset =
          targetTable && targetColumn ? columnRowCenterOffset(targetTable, targetColumn.id, canvas.viewSettings) : null;
        const route = getEdgeRoute(canvas, resolved.foreignKey.id);
        const data: FkEdgeData = {
          foreignKey: resolved.foreignKey,
          sourceRowOffset,
          targetRowOffset,
          sourceCardinality: sourceTable ? computeForeignKeyCardinality(sourceTable, resolved.foreignKey) : 'many',
          showCardinalityLabel: canvas.viewSettings.showCardinalityLabel,
          waypoints: route.waypoints,
          isManuallyRouted: route.isManuallyRouted,
          isEndpointTableSelected:
            selectedTableIds.includes(resolved.sourceTableId) || selectedTableIds.includes(resolved.targetTableId),
        };
        return [
          {
            id: resolved.foreignKey.id,
            type: 'foreignKey' as const,
            source: resolved.sourceTableId,
            target: resolved.targetTableId,
            sourceHandle: sourceColumn && sourceRowOffset !== null ? sourceColumn.id : '__table__',
            targetHandle: targetColumn && targetRowOffset !== null ? targetColumn.id : '__table__',
            selectable: true,
            focusable: true,
            reconnectable: true,
            selected: edgesRef.current.find((candidate) => candidate.id === resolved.foreignKey.id)?.selected ?? false,
            data,
          },
        ];
      }),
    );
  }, [
    canvas,
    schema,
    groupBoxById,
    tableSizes,
    effectiveTablePositions,
    selectedTableIds,
    inspectorTarget,
    dragOriginColumn,
    execute,
    changeNoteText,
    deleteNote,
    unpinNote,
    openNotePinMenu,
    setNodes,
    setEdges,
  ]);

  // Canvas -> sdílený výběr (E11-US4/US5): rubber-band/klik na canvasu se
  // promítne do stromu i inspektoru stejnou cestou jako výběr ve stromu.
  const handleSelectionChange = useCallback<OnSelectionChangeFunc>(
    ({ nodes: selected, edges: selectedEdges }) => {
      const tableIds = selected.filter((node) => node.type === 'table').map((node) => node.id);
      const groupIds = selected.filter((node) => node.type === 'groupFrame').map((node) => node.id);
      const noteIds = selected.filter((node) => node.type === 'note').map((node) => node.id);
      setSelectedTableIds(tableIds);
      if (tableIds.length === 1 && groupIds.length === 0 && noteIds.length === 0 && selectedEdges.length === 0) {
        setInspectorTarget({ kind: 'table', tableId: tableIds[0] });
      } else if (groupIds.length === 1 && tableIds.length === 0 && noteIds.length === 0 && selectedEdges.length === 0) {
        setInspectorTarget({ kind: 'group', groupId: groupIds[0] });
      } else if (noteIds.length === 1 && tableIds.length === 0 && groupIds.length === 0 && selectedEdges.length === 0) {
        setInspectorTarget({ kind: 'note', noteId: noteIds[0] });
      } else if (
        selectedEdges.length === 1 &&
        tableIds.length === 0 &&
        groupIds.length === 0 &&
        noteIds.length === 0
      ) {
        const edge = selectedEdges[0] as CanvasEdge;
        setInspectorTarget({ kind: 'foreignKey', tableId: edge.source, foreignKeyId: (edge.data as FkEdgeData).foreignKey.id });
      } else {
        setInspectorTarget({ kind: 'none' });
      }
    },
    [setSelectedTableIds, setInspectorTarget],
  );

  /** Sdíleno mezi Delete klávesou (`handleEdgesChange`) a kontextovým menu vazby (E5-US5). */
  const deleteForeignKeyEdge = useCallback(
    (edge: CanvasEdge) => {
      const table = schema.tables.find((candidate) => candidate.id === edge.source);
      const foreignKey = table?.foreignKeys.find((candidate) => candidate.id === (edge.data as FkEdgeData).foreignKey.id);
      if (!table || !foreignKey) return;
      schemaExecute(createDeleteForeignKeyCommand(table.id, foreignKey, table.foreignKeys.indexOf(foreignKey)));
    },
    [schema, schemaExecute],
  );

  // Vazby jsou odvozený stav ze schématu (stejně jako `edges` výše), ne RF-vlastněný
  // stav — proto se `remove` změna (Delete klávesa, E5-US5) NEAPLIKUJE přímo přes
  // `onEdgesChange`, ale přemapuje na `schemaExecute(createDeleteForeignKeyCommand)`;
  // derive-efekt pak hranu sám zmizí, jakmile se `schema` změní.
  const handleEdgesChange = useCallback(
    (changes: EdgeChange<CanvasEdge>[]) => {
      const passthrough = changes.filter((change) => {
        if (change.type !== 'remove') return true;
        const edge = edges.find((candidate) => candidate.id === change.id);
        if (edge) deleteForeignKeyEdge(edge);
        return false;
      });
      if (passthrough.length > 0) onEdgesChange(passthrough);
    },
    [edges, deleteForeignKeyEdge, onEdgesChange],
  );

  // E5-US1 — zdroj tažení (drag START = referencovaný/PK sloupec, epic-5 kap. 2),
  // uložen jen pro zvýraznění kompatibilních cílů v `TableNode` během tažení.
  const handleConnectStart = useCallback<OnConnectStart>(
    (_event, params) => {
      if (!params.nodeId || !params.handleId || params.handleId === '__table__') {
        setDragOriginColumn(null);
        return;
      }
      const originTable = schema.tables.find((candidate) => candidate.id === params.nodeId);
      const originColumn = originTable?.columns.find((candidate) => candidate.id === params.handleId);
      if (!originTable || !originColumn) {
        setDragOriginColumn(null);
        return;
      }
      setDragOriginColumn({
        tableId: originTable.id,
        columnId: originColumn.id,
        type: originColumn.canonicalType ?? originColumn.rawType,
      });
    },
    [schema],
  );

  // E5-US1/US6 — `connectionState.fromHandle`/`toHandle` dávají doslovný
  // začátek/konec gesta (ne RF-normalizovaný `Connection.source/target`, který
  // by roli obrátil podle deklarovaného typu handle — viz plán/epic-5 kap. 2:
  // drag START = referencovaný/PK rodič, drag END = referencující/FK potomek).
  //
  // POZOR: RF interně přepojení existující hrany (`onReconnect*`) implementuje
  // nad STEJNÝM connection-trackingem jako nové spojení — `onConnectEnd` tedy
  // vedle `onReconnectEnd` vždy doběhne i PRO gesto přepojení (potvrzeno
  // manuálním průchodem: bez tohoto guardu vzniklo z jednoho tažení za konec
  // hrany jak správné přepojení, tak navíc DRUHÁ, nechtěná nová FK vazba).
  // `isReconnectingRef` (nastaví `onReconnectStart`, uklidí `handleReconnectEnd`)
  // tomu zabrání.
  const handleConnectEnd = useCallback<OnConnectEnd>(
    (event, connectionState) => {
      setDragOriginColumn(null);
      if (isReconnectingRef.current) return;
      const fromHandle = connectionState.fromHandle;
      const toHandle = connectionState.toHandle;
      if (!fromHandle?.id || !toHandle?.id) return;
      if (fromHandle.nodeId === toHandle.nodeId && fromHandle.id === toHandle.id) return;

      const referencedTable = schema.tables.find((candidate) => candidate.id === fromHandle.nodeId);
      const referencedColumn = referencedTable?.columns.find((candidate) => candidate.id === fromHandle.id);
      const referencingTable = schema.tables.find((candidate) => candidate.id === toHandle.nodeId);
      const referencingColumn = referencingTable?.columns.find((candidate) => candidate.id === toHandle.id);
      if (!referencedTable || !referencedColumn || !referencingTable || !referencingColumn) return;

      if (!areForeignKeyColumnTypesCompatible(referencingColumn, referencedColumn)) {
        showConnectFeedback(
          event,
          t('canvas.fk.typeMismatch', {
            source: referencingColumn.name,
            target: referencedColumn.name,
          }),
        );
        return;
      }

      const foreignKey: ForeignKeyModel = {
        id: randomId(),
        name: defaultForeignKeyName(referencingTable, [referencingColumn.name]),
        columns: [referencingColumn.name],
        referencedSchema: referencedTable.schema,
        referencedTable: referencedTable.name,
        referencedColumns: [referencedColumn.name],
        onDelete: 'NO ACTION',
        onUpdate: 'NO ACTION',
      };

      // E7-US3b — tabulka, která tímhle gestem dostává SVOU PRVNÍ FK vazbu
      // (na kterékoli straně), se rovnou přemístí blíž k protistraně —
      // spočítáno na stavu PŘED přidáním vazby, `runGroup` spojí přesun s
      // přidáním FK do jednoho vratného kroku, jen když k umístění opravdu
      // dojde (běžný případ beze změny — jediné `schemaExecute` volání).
      const placements = computePlacementsForNewConnection(
        schema,
        effectiveTablePositions,
        tableSizes,
        referencingTable.id,
        referencedTable.id,
      );
      if (placements.length > 0) {
        runGroup({ key: 'schema.command.addForeignKey', params: { name: foreignKey.name } }, () => {
          schemaExecute(createAddForeignKeyCommand(referencingTable.id, foreignKey));
          for (const placement of placements) {
            execute(createPlaceTableCommand(placement.tableId, canvas.tablePositions[placement.tableId], placement.position));
          }
        });
      } else {
        schemaExecute(createAddForeignKeyCommand(referencingTable.id, foreignKey));
      }
      setInspectorTarget({ kind: 'foreignKey', tableId: referencingTable.id, foreignKeyId: foreignKey.id });
    },
    [schema, schemaExecute, setInspectorTarget, showConnectFeedback, t, effectiveTablePositions, tableSizes, runGroup, execute, canvas.tablePositions],
  );

  // E5-US4 — `handleType` je role KONCE PŮVODNÍ hrany, který uživatel uchopil
  // ('source' = referencující/FK konec, 'target' = referencovaný/PK konec,
  // stejná konvence jako u vykreslené hrany výše); `connectionState.toHandle`
  // dává nové umístění TOHOTO konce, druhý konec zůstává beze změny.
  const reconnectFinalConnection = useCallback(
    (event: MouseEvent | TouchEvent, oldEdge: CanvasEdge, handleType: HandleType, connectionState: FinalConnectionState) => {
      const toHandle = connectionState.toHandle;
      if (!toHandle?.id) return;

      const ownerTable = schema.tables.find((candidate) => candidate.id === oldEdge.source);
      const foreignKey = ownerTable?.foreignKeys.find(
        (candidate) => candidate.id === (oldEdge.data as FkEdgeData).foreignKey.id,
      );
      if (!ownerTable || !foreignKey) return;
      if (foreignKey.columns.length !== 1) {
        showConnectFeedback(event, t('canvas.fk.compositeReconnectUnsupported'));
        return;
      }

      const newTable = schema.tables.find((candidate) => candidate.id === toHandle.nodeId);
      const newColumn = newTable?.columns.find((candidate) => candidate.id === toHandle.id);
      if (!newTable || !newColumn) return;

      if (handleType === 'target') {
        // Referencovaný (PK/rodičovský) konec se přesouvá na nový sloupec/tabulku.
        const referencingColumn = ownerTable.columns.find((candidate) => candidate.name === foreignKey.columns[0]);
        if (!referencingColumn || !areForeignKeyColumnTypesCompatible(referencingColumn, newColumn)) {
          showConnectFeedback(event, t('canvas.fk.typeMismatch', { source: referencingColumn?.name ?? '', target: newColumn.name }));
          return;
        }
        schemaExecute(
          createUpdateForeignKeyCommand(ownerTable.id, foreignKey.id, foreignKey, {
            ...foreignKey,
            referencedSchema: newTable.schema,
            referencedTable: newTable.name,
            referencedColumns: [newColumn.name],
          }),
        );
        return;
      }

      // Referencující (FK/potomkovský) konec se přesouvá — může jít o jiný sloupec
      // TÉŽE tabulky (přejmenování páru), nebo o přesun vazby na JINOU tabulku
      // (delete+add, stejný vzor jako `DeleteTableDialog.tsx` pro schema+canvas).
      if (newTable.id === ownerTable.id && newColumn.name === foreignKey.columns[0]) return;
      const referencedTable = schema.tables.find(
        (candidate) => candidate.schema === foreignKey.referencedSchema && candidate.name === foreignKey.referencedTable,
      );
      const referencedColumn = referencedTable?.columns.find(
        (candidate) => candidate.name === foreignKey.referencedColumns[0],
      );
      if (!referencedColumn || !areForeignKeyColumnTypesCompatible(newColumn, referencedColumn)) {
        showConnectFeedback(event, t('canvas.fk.typeMismatch', { source: newColumn.name, target: referencedColumn?.name ?? '' }));
        return;
      }

      if (newTable.id === ownerTable.id) {
        schemaExecute(
          createUpdateForeignKeyCommand(ownerTable.id, foreignKey.id, foreignKey, {
            ...foreignKey,
            columns: [newColumn.name],
          }),
        );
      } else {
        // Přepojení FK konce na jinou tabulku = delete+add (jiný vlastník), ale
        // pro uživatele je to JEDNO gesto ("přepojení") — runGroup je spojí do
        // jednoho vratného kroku (E13-US4), místo dvou samostatných undo kroků.
        const name = ensureUniqueName(foreignKey.name, newTable.foreignKeys.map((candidate) => candidate.name));
        runGroup({ key: 'schema.command.updateForeignKey', params: { name: foreignKey.name } }, () => {
          schemaExecute(createDeleteForeignKeyCommand(ownerTable.id, foreignKey, ownerTable.foreignKeys.indexOf(foreignKey)));
          schemaExecute(
            createAddForeignKeyCommand(newTable.id, { ...foreignKey, id: randomId(), name, columns: [newColumn.name] }),
          );
        });
      }
    },
    [schema, schemaExecute, showConnectFeedback, t, runGroup],
  );

  // `onReconnectEnd` vždy doběhne i pro `onConnectEnd` (viz komentář u
  // `handleConnectEnd`) — `isReconnectingRef` po dobu gesta (nastaví
  // `onReconnectStart`) donutí `handleConnectEnd` svou část vynechat; `finally`
  // ho uklidí bez ohledu na to, kterým z `return` výše `reconnectFinalConnection` skončí.
  const handleReconnectEnd = useCallback(
    (event: MouseEvent | TouchEvent, oldEdge: CanvasEdge, handleType: HandleType, connectionState: FinalConnectionState) => {
      try {
        reconnectFinalConnection(event, oldEdge, handleType, connectionState);
      } finally {
        isReconnectingRef.current = false;
      }
    },
    [reconnectFinalConnection],
  );

  // Fokus ze stromu (E11-US3) — jen `focusToken` v dependencích, aby stejný cíl
  // zvolený podruhé za sebou pořád vyvolal nový pan/zoom.
  useEffect(() => {
    if (!focusTarget) return;
    fitView({ nodes: [{ id: focusTarget.tableId }], duration: 400, maxZoom: 1.5, padding: 0.35 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusToken]);

  const handleNodeDragStop = useCallback(
    (_event: MouseEvent | TouchEvent, node: CanvasNode) => {
      if (node.type === 'table') {
        const from = effectiveTablePositions[node.id];
        if (from.x !== node.position.x || from.y !== node.position.y) {
          execute(createMoveTableCommand(node.id, from, node.position));
        }
      } else if (node.type === 'groupFrame') {
        const originalBox = node.data.box;
        const delta = { x: node.position.x - originalBox.x, y: node.position.y - originalBox.y };
        if (delta.x !== 0 || delta.y !== 0) {
          const moves = node.data.group.memberTableIds.map((tableId) => {
            const from = effectiveTablePositions[tableId];
            return { tableId, from, to: { x: from.x + delta.x, y: from.y + delta.y } };
          });
          execute(createMoveGroupCommand(moves));
        }
      } else if (node.type === 'note') {
        const note = canvas.notes.find((candidate) => candidate.id === node.id);
        if (note?.attachment.type === 'none' && note.position) {
          if (note.position.x !== node.position.x || note.position.y !== node.position.y) {
            execute(createMoveNoteCommand(node.id, note.position, node.position));
          }
        }
      }
    },
    [canvas, effectiveTablePositions, execute],
  );

  const createNoteWithAttachment = useCallback(
    (attachment: NoteAttachment, position?: { x: number; y: number }) => {
      const note: Note = { id: randomId(), text: '', attachment, ...(position ? { position } : {}) };
      execute(createNoteCommand(note));
      closeMenu();
    },
    [execute, closeMenu],
  );

  const handleCreateGroup = useCallback(() => {
    const ungrouped = selectedTableIds.filter(
      (tableId) => !canvas.groups.some((group) => group.memberTableIds.includes(tableId)),
    );
    if (ungrouped.length >= 2) setGroupDialog({ mode: 'create', tableIds: ungrouped });
  }, [selectedTableIds, canvas.groups]);

  const handleDissolveGroup = useCallback(
    (groupId: string) => {
      const group = canvas.groups.find((candidate) => candidate.id === groupId);
      if (!group) return;
      const box = groupBoxById[groupId];
      const unpinnedNotes = canvas.notes
        .filter((note) => note.attachment.type === 'group' && note.attachment.id === groupId)
        .map((note, index) => ({
          noteId: note.id,
          position: box ? { x: box.x + box.width + 24, y: box.y + index * NOTE_STACK_OFFSET } : { x: 0, y: 0 },
        }));
      execute(createDissolveGroupCommand(group, unpinnedNotes));
      closeMenu();
    },
    [canvas.groups, canvas.notes, groupBoxById, execute, closeMenu],
  );

  const eligibleGroupsFor = (tableId: string): Group[] =>
    canvas.groups.filter((group) => !group.memberTableIds.includes(tableId));

  return (
    <div className="canvas-shell">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        onNodesChange={onNodesChange}
        onEdgesChange={handleEdgesChange}
        onNodeDragStop={handleNodeDragStop}
        onSelectionChange={handleSelectionChange}
        onPaneClick={() => {
          closeMenu();
          setConnectFeedback(null);
        }}
        onPaneContextMenu={(event) => {
          event.preventDefault();
          const point = 'clientX' in event ? event : (event as unknown as MouseEvent);
          const flowPosition = screenToFlowPosition({ x: point.clientX, y: point.clientY });
          setMenu({ kind: 'pane', flowPosition, position: { x: point.clientX, y: point.clientY } });
        }}
        // E5-US1/US6 — libovolný handle může jak zahájit, tak přijmout tažení
        // (loose mode); jediné pravidlo, které RF sám vynucuje, je zákaz
        // spojení sloupce se sebou samým (stejný handle na obou koncích).
        connectionMode={ConnectionMode.Loose}
        onConnectStart={handleConnectStart}
        onConnectEnd={handleConnectEnd}
        onConnect={() => {}}
        // E5-US4 — `onReconnect` musí být funkce, aby RF vůbec povolilo tažení
        // koncového bodu existující hrany; skutečná logika je v `onReconnectEnd`
        // (viz komentář u `handleReconnectEnd`).
        onReconnect={() => {}}
        onReconnectStart={handleReconnectStart}
        onReconnectEnd={handleReconnectEnd}
        onEdgeContextMenu={(event, edge) => {
          event.preventDefault();
          const canvasEdge = edge as CanvasEdge;
          setMenu({
            kind: 'edge',
            edgeId: canvasEdge.id,
            tableId: canvasEdge.source,
            foreignKeyId: (canvasEdge.data as FkEdgeData).foreignKey.id,
            position: { x: event.clientX, y: event.clientY },
          });
        }}
        // E5-US5 — výběr vazby + Delete/Backspace (`handleEdgesChange` výše
        // přemapuje na `createDeleteForeignKeyCommand`, bez potvrzovacího
        // dialogu — epic-5 kap. 2 / precedens Epic 4, viz plán).
        deleteKeyCode={['Delete', 'Backspace']}
        // AC E6-US8/E6-US4 vyžaduje shift+klik i rubber-band výběr — RF defaultně
        // multi-select váže na Ctrl/Cmd, sjednoceno na Shift pro obojí.
        multiSelectionKeyCode="Shift"
        fitView
      >
        <Background />
        <Controls showInteractive={false} />
        <Panel position="top-left">
          <div className="canvas-save-controls">
            {/* E13-US2/US3 — tlačítka neaktivní na prázdném zásobníku, tooltip
                ukazuje popis operace, kterou vrátí/zopakují (i18n `descriptionKey`
                na commandu, žádné nové klíče pro stávající commandy). */}
            <button
              type="button"
              disabled={!canUndo}
              onClick={undo}
              title={undoEntry ? t('undo.tooltip', { description: t(undoEntry.descriptionKey, undoEntry.descriptionParams) }) : undefined}
            >
              <Undo2 size={16} />
              {t('undo.button')}
            </button>
            <button
              type="button"
              disabled={!canRedo}
              onClick={redo}
              title={redoEntry ? t('redo.tooltip', { description: t(redoEntry.descriptionKey, redoEntry.descriptionParams) }) : undefined}
            >
              <Redo2 size={16} />
              {t('redo.button')}
            </button>
            <button type="button" onClick={saveNow}>
              <Save size={16} />
              {t('canvas.saveNow')}
            </button>
            <span className={`save-status save-status--${saveStatus}`}>{t(`canvas.saveStatus.${saveStatus}`)}</span>
          </div>
        </Panel>
        {selectedTableIds.length >= 2 && (
          <Panel position="top-center">
            <button type="button" className="primary" onClick={handleCreateGroup}>
              <Users size={16} />
              {t('canvas.group.createFromSelection')}
            </button>
          </Panel>
        )}
      </ReactFlow>

      {/*
       * Epic 15, E15-US4 — dřívější pravý horní panel canvasu
       * (`.canvas-view-controls`, `<Panel position="top-right">`) nahrazen
       * jedním rozbalovacím menu portálovaným do `.toolbar__actions`
       * (`ProjectPage.tsx`). Žádná z položek nemění chování/oprávnění, jen
       * umístění (`CanvasToolsMenu.tsx` — proč `children` zůstávají vždy
       * namountované).
       */}
      <CanvasToolsMenu portalTarget={toolsMenuSlot}>
        <ViewSettingsPanel viewSettings={canvas.viewSettings} onChange={updateViewSettings} />
        <button type="button" onClick={() => setAutoLayoutConfirmOpen(true)}>
          <LayoutGrid size={16} />
          {t('canvas.autoLayout.button')}
        </button>
        <SyncPanel
          ref={syncPanelRef}
          projectId={projectId}
          schema={schema}
          canvas={canvas}
          schemaExecute={schemaExecute}
          canvasExecute={execute}
          effectiveTablePositions={effectiveTablePositions}
          tableSizes={tableSizes}
        />
        <ChangeScriptPanel
          projectId={projectId}
          schema={schema}
          canvas={canvas}
          schemaExecute={schemaExecute}
          canvasExecute={execute}
          effectiveTablePositions={effectiveTablePositions}
          tableSizes={tableSizes}
          onRequestSync={requestSync}
        />
        <CreateScriptPanel projectId={projectId} schema={schema} />
        <ExportPanel
          projectName={projectName}
          schema={schema}
          canvas={canvas}
          effectiveTablePositions={effectiveTablePositions}
          tableSizes={tableSizes}
        />
      </CanvasToolsMenu>

      {autoLayoutConfirmOpen && (
        <ConfirmDialog
          title={t('canvas.autoLayout.confirmTitle')}
          message={<p>{t('canvas.autoLayout.confirmMessage')}</p>}
          confirmLabel={t('canvas.autoLayout.confirmAction')}
          confirmDisabled={autoLayoutBusy}
          onCancel={() => setAutoLayoutConfirmOpen(false)}
          onConfirm={() => void confirmAutoLayout()}
        />
      )}

      {menu?.kind === 'table' && (
        <ContextMenu position={menu.position} onClose={closeMenu}>
          <ContextMenuItem
            icon={<StickyNote size={14} />}
            label={t('canvas.note.addToTable')}
            onSelect={() => createNoteWithAttachment({ type: 'table', id: menu.tableId })}
          />
          <ContextMenuItem
            icon={<Trash2 size={14} />}
            label={t('inspector.table.deleteButton')}
            danger
            onSelect={() => {
              setDeleteTableId(menu.tableId);
              closeMenu();
            }}
          />
          {(() => {
            const groupId = canvas.groups.find((group) => group.memberTableIds.includes(menu.tableId))?.id;
            if (groupId) {
              const group = canvas.groups.find((candidate) => candidate.id === groupId)!;
              return (
                <ContextMenuItem
                  icon={<Ungroup size={14} />}
                  label={t('canvas.group.removeTable')}
                  onSelect={() => {
                    execute(
                      createRemoveTableFromGroupCommand(
                        groupId,
                        menu.tableId,
                        group.memberTableIds.indexOf(menu.tableId),
                      ),
                    );
                    closeMenu();
                  }}
                />
              );
            }
            return eligibleGroupsFor(menu.tableId).map((group) => (
              <ContextMenuItem
                key={group.id}
                icon={<Plus size={14} />}
                label={t('canvas.group.addTable', { name: group.name })}
                onSelect={() => {
                  execute(createAddTableToGroupCommand(group.id, menu.tableId));
                  closeMenu();
                }}
              />
            ));
          })()}
        </ContextMenu>
      )}

      {menu?.kind === 'column' && (
        <ContextMenu position={menu.position} onClose={closeMenu}>
          <ContextMenuItem
            icon={<StickyNote size={14} />}
            label={t('canvas.note.addToColumn')}
            onSelect={() => createNoteWithAttachment({ type: 'column', id: menu.columnId })}
          />
          <ContextMenuItem
            icon={<Trash2 size={14} />}
            label={t('inspector.column.deleteButton')}
            danger
            onSelect={() => {
              setDeleteColumn({ tableId: menu.tableId, columnId: menu.columnId });
              closeMenu();
            }}
          />
        </ContextMenu>
      )}

      {menu?.kind === 'edge' && (
        <ContextMenu position={menu.position} onClose={closeMenu}>
          {getEdgeRoute(canvas, menu.foreignKeyId).isManuallyRouted && (
            <ContextMenuItem
              icon={<RotateCcw size={14} />}
              label={t('canvas.fk.resetRoute')}
              onSelect={() => {
                execute(createResetEdgeRouteCommand(menu.foreignKeyId, getEdgeRoute(canvas, menu.foreignKeyId)));
                closeMenu();
              }}
            />
          )}
          <ContextMenuItem
            icon={<Trash2 size={14} />}
            label={t('canvas.fk.deleteEdge')}
            danger
            onSelect={() => {
              const edge = edges.find((candidate) => candidate.id === menu.edgeId);
              if (edge) deleteForeignKeyEdge(edge);
              closeMenu();
            }}
          />
        </ContextMenu>
      )}

      {menu?.kind === 'group' && (
        <ContextMenu position={menu.position} onClose={closeMenu}>
          <ContextMenuItem
            icon={<Pencil size={14} />}
            label={t('canvas.group.rename')}
            onSelect={() => {
              const group = canvas.groups.find((candidate) => candidate.id === menu.groupId);
              if (group) setGroupDialog({ mode: 'rename', groupId: menu.groupId, currentName: group.name });
              closeMenu();
            }}
          />
          <ContextMenuItem
            icon={<StickyNote size={14} />}
            label={t('canvas.note.addToGroup')}
            onSelect={() => createNoteWithAttachment({ type: 'group', id: menu.groupId })}
          />
          <ContextMenuItem
            icon={<Ungroup size={14} />}
            label={t('canvas.group.dissolve')}
            danger
            onSelect={() => handleDissolveGroup(menu.groupId)}
          />
        </ContextMenu>
      )}

      {menu?.kind === 'note' &&
        (() => {
          const note = canvas.notes.find((candidate) => candidate.id === menu.noteId);
          if (!note) return null;
          return (
            <ContextMenu position={menu.position} onClose={closeMenu}>
              {note.attachment.type !== 'none' && (
                <ContextMenuItem
                  icon={<PinOff size={14} />}
                  label={t('canvas.note.unpin')}
                  onSelect={() => {
                    unpinNote(menu.noteId);
                    closeMenu();
                  }}
                />
              )}
              {schema.tables
                .filter((table) => !(note.attachment.type === 'table' && note.attachment.id === table.id))
                .map((table) => (
                  <ContextMenuItem
                    key={`table-${table.id}`}
                    icon={<Pin size={14} />}
                    label={t('canvas.note.pinToTable', { name: table.name })}
                    onSelect={() => {
                      execute(
                        createPinNoteCommand(
                          menu.noteId,
                          { attachment: note.attachment, position: note.position },
                          { attachment: { type: 'table', id: table.id } },
                        ),
                      );
                      closeMenu();
                    }}
                  />
                ))}
              {canvas.groups
                .filter((group) => !(note.attachment.type === 'group' && note.attachment.id === group.id))
                .map((group) => (
                  <ContextMenuItem
                    key={`group-${group.id}`}
                    icon={<Pin size={14} />}
                    label={t('canvas.note.pinToGroup', { name: group.name })}
                    onSelect={() => {
                      execute(
                        createPinNoteCommand(
                          menu.noteId,
                          { attachment: note.attachment, position: note.position },
                          { attachment: { type: 'group', id: group.id } },
                        ),
                      );
                      closeMenu();
                    }}
                  />
                ))}
            </ContextMenu>
          );
        })()}

      {menu?.kind === 'pane' && (
        <ContextMenu position={menu.position} onClose={closeMenu}>
          <ContextMenuItem
            icon={<Plus size={14} />}
            label={t('schema.table.addAction')}
            onSelect={() => {
              const table = createDefaultTable(schema);
              schemaExecute(createAddTableCommand(table));
              setInspectorTarget({ kind: 'table', tableId: table.id });
              closeMenu();
            }}
          />
          <ContextMenuItem
            icon={<StickyNote size={14} />}
            label={t('canvas.note.addFree')}
            onSelect={() => createNoteWithAttachment({ type: 'none' }, menu.flowPosition)}
          />
        </ContextMenu>
      )}

      {deleteTableId &&
        (() => {
          const table = schema.tables.find((candidate) => candidate.id === deleteTableId);
          if (!table) return null;
          return (
            <DeleteTableDialog
              table={table}
              schema={schema}
              canvas={canvas}
              schemaExecute={schemaExecute}
              canvasExecute={execute}
              onClose={() => setDeleteTableId(null)}
            />
          );
        })()}

      {deleteColumn &&
        (() => {
          const table = schema.tables.find((candidate) => candidate.id === deleteColumn.tableId);
          const column = table?.columns.find((candidate) => candidate.id === deleteColumn.columnId);
          if (!table || !column) return null;
          return (
            <DeleteColumnDialog
              table={table}
              column={column}
              schema={schema}
              canvas={canvas}
              schemaExecute={schemaExecute}
              canvasExecute={execute}
              onClose={() => setDeleteColumn(null)}
            />
          );
        })()}

      {groupDialog?.mode === 'create' && (
        <GroupNameDialog
          title={t('canvas.group.createTitle')}
          confirmLabel={t('common.create')}
          initialName={t('canvas.group.defaultName')}
          onCancel={() => setGroupDialog(null)}
          onConfirm={(name) => {
            execute(createGroupCommand({ id: randomId(), name, memberTableIds: groupDialog.tableIds }));
            setGroupDialog(null);
          }}
        />
      )}

      {groupDialog?.mode === 'rename' && (
        <GroupNameDialog
          title={t('canvas.group.renameTitle')}
          confirmLabel={t('canvas.group.rename')}
          initialName={groupDialog.currentName}
          onCancel={() => setGroupDialog(null)}
          onConfirm={(name) => {
            execute(createRenameGroupCommand(groupDialog.groupId, groupDialog.currentName, name));
            setGroupDialog(null);
          }}
        />
      )}

      {connectFeedback && (
        <div
          className="canvas-connect-feedback"
          style={{ left: connectFeedback.position.x, top: connectFeedback.position.y }}
        >
          {connectFeedback.message}
        </div>
      )}
    </div>
  );
}
